/**
 * Brand utility (SEC-334 white-label)
 *
 * Holds the active brand of this page and applies it to the document (tab icon,
 * title, colours, fonts). Sits next to siteContext.js, which does the same for
 * the country (se / no). The two are independent.
 *
 * The active brand starts as the copy cached in browser storage (instant paint),
 * or Securdia. utils/brandLoader.js then asks the API and updates it in place.
 */
import Vue from "vue";
import { SECURDIA, DEFAULT_BRAND_KEY, brandFromRecord } from "@/constants/brands";

// The product name the pages are written with. A brand that chooses another one has it
// swapped in as each page sets its title (see useBrandProductName).
const DEFAULT_PRODUCT_NAME = SECURDIA.productName;
let productNameApplied = false;
import store from "@/store";
import i18n from "@/i18n";

// Browser-storage copy of the last brand record the API returned for this web address
export const BRAND_CACHE_KEY = "securcloud_brand_record";
// Developer override: which hostname to ask the API for, e.g. localStorage.setItem('securcloud_brand_host', 'portal.csg.se')
export const DEV_HOST_OVERRIDE_KEY = "securcloud_brand_host";

function readCachedRecord() {
  try {
    const raw = window.localStorage.getItem(BRAND_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Has the API already told us the brand of this web address once?
 * Browser storage is per address, so the answer is per address too.
 */
export function hasCachedBrand() {
  return readCachedRecord() !== null;
}

function initialBrand() {
  const cached = readCachedRecord();
  if (cached && cached.key && !cached.isDefault) return brandFromRecord(cached);
  return { ...SECURDIA };
}

// One reactive object for the whole app; components read it as this.$brand
const activeBrand = Vue.observable(initialBrand());

export function getBrand() {
  return activeBrand;
}

export function isDefaultBrand() {
  return activeBrand.isDefault === true;
}

/**
 * Replace the active brand with what the API returned. Updates in place so
 * every component that shows this.$brand re-renders.
 */
export function setBrandFromApi(payload) {
  const next = payload && !payload.isDefault ? brandFromRecord(payload) : { ...SECURDIA };
  Object.assign(activeBrand, next);
  return activeBrand;
}

// Customer and business logins. Only these see the agreement and add-on views of a customer.
const CUSTOMER_ROLES = ["superuser", "user"];

/**
 * Is this a customer or business login whose customer was sold by a white label reseller?
 *
 * Such a reseller sells its own agreements and add-on services, so its customers must not
 * see Securdia's: the agreement view and the add-on view are hidden for them. The session's
 * brand is what the API resolved for the user at login (utils/brands.getUserBrandKey): the
 * id of a reseller with white label on and branding filled in, otherwise Securdia. A
 * reseller with the toggle on but no branding is not a brand, so its customers keep both
 * views. Sessions from before white label have no brand and are Securdia.
 */
export function isWhiteLabelCustomer(user) {
  if (!user || !CUSTOMER_ROLES.includes(user.role)) return false;
  return (user.brand || DEFAULT_BRAND_KEY) !== DEFAULT_BRAND_KEY;
}

// Routes hidden for white label customers (see isWhiteLabelCustomer)
export const WHITE_LABEL_HIDDEN_ROUTES = ["customer_agreements", "addonBuy"];

/**
 * Support e-mail shown to customers on this brand's pages.
 * Securdia keeps the country rule (se / no), a partner brand has one address.
 */
export function getBrandSupportEmail(securdiaEmail) {
  if (activeBrand.isDefault) return securdiaEmail;
  return activeBrand.contact.email;
}

// "#25292C" -> "37, 41, 44" so Tailwind's opacity utilities keep working
function hexToRgbList(hex) {
  const clean = String(hex || "").replace("#", "");
  if (clean.length !== 6) return "";
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

/**
 * Page titles are written in the translations and in a handful of components, and they all
 * end in the default product name, for example "Kund | SecurCloud". A brand that has chosen
 * its own product name needs that swapped everywhere a title is set, which is dozens of
 * places. Rather than editing them all, the swap happens as the title is assigned.
 *
 * Only a brand with a different product name reaches this; Securdia never does, so its
 * titles are untouched.
 */
function useBrandProductName(productName) {
  if (productNameApplied) return;
  if (!productName || productName === DEFAULT_PRODUCT_NAME) return;

  const native = Object.getOwnPropertyDescriptor(Document.prototype, "title");
  if (!native || !native.set) return; // very old browser, leave the title alone

  Object.defineProperty(document, "title", {
    configurable: true,
    get() {
      return native.get.call(document);
    },
    set(value) {
      native.set.call(document, String(value).split(DEFAULT_PRODUCT_NAME).join(productName));
    },
  });

  productNameApplied = true;
}

function setFontStylesheet(url) {
  let link = document.getElementById("brand-fonts");
  if (!url) {
    if (link) link.remove();
    return;
  }
  if (!link) {
    link = document.createElement("link");
    link.id = "brand-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.getAttribute("href") !== url) link.setAttribute("href", url);
}

// SEC-334: set by the language switcher when a visitor picks a language themselves.
// Browser storage is per web address, so this only ever describes the address it is on.
export const LOCALE_CHOSEN_KEY = "securcloud_locale_chosen";

/**
 * SEC-334: start the portal in the brand's own language.
 *
 * A white-label brand can choose the language its portal opens in (Branding window,
 * "Portal language"). It is decided by the web address through its brand, so it applies to
 * that one partner's address and nothing assumes a whole domain speaks one language.
 *
 * Only a starting point: a visitor who picked a language from the menu keeps their choice.
 * Securdia, and a brand that left it empty, keep the existing rule unchanged.
 */
export function applyBrandStartingLanguage(brand) {
  const language = brand && !brand.isDefault && brand.portalLanguage;
  if (!language) return;

  let chosen = false;
  try {
    chosen = window.localStorage.getItem(LOCALE_CHOSEN_KEY) === "1";
  } catch (e) {
    // storage blocked: nothing was chosen that we could remember
  }
  if (chosen) return;

  try {
    window.localStorage.setItem("securcloud_locale", language);
  } catch (e) {
    // storage blocked: the language still applies for this visit
  }
  store.commit("SET_APP_LOCALE", language);
  i18n.locale = language;
}

/**
 * Apply the brand to the page: data-brand marker (activates the colour and font
 * override stylesheet), CSS variables, tab title and tab icon.
 * For Securdia only the marker is set, nothing else changes.
 */
export function applyBrandToDocument(brand) {
  const html = document.documentElement;
  html.setAttribute("data-brand", brand.key);

  if (brand.isDefault) return;

  // Step 1: colours as CSS variables, read by styles/brands/brand-overrides.scss
  const shades = (brand.colors && brand.colors.primary) || {};
  for (const shade of Object.keys(shades)) {
    html.style.setProperty(`--brand-primary-${shade}-rgb`, hexToRgbList(shades[shade]));
  }
  html.style.setProperty("--brand-accent-rgb", hexToRgbList(brand.colors && brand.colors.accent));
  html.style.setProperty("--brand-overlay", (brand.colors && brand.colors.overlay) || "transparent");

  // Step 2: fonts
  const typography = brand.typography || {};
  html.style.setProperty("--brand-font-serif", `'${typography.headingFont || "Source Serif Pro"}', serif`);
  html.style.setProperty("--brand-font-sans", `'${typography.textFont || "Source Sans Pro"}', sans-serif`);
  setFontStylesheet(typography.stylesheetUrl);

  // Step 3: tab title and tab icon. The product name is swapped into every later title too.
  useBrandProductName(brand.productName);
  document.title = brand.productName;
  const iconLink = document.querySelector("link[rel~='icon']");
  if (iconLink) iconLink.setAttribute("href", brand.favicon);
}

/**
 * A stored login that belongs to another brand is cleared, so the visitor
 * lands on the login page of this brand. Existing sessions without a brand
 * value are Securdia sessions.
 * @returns {boolean} true when a session was cleared
 */
export function clearStaleBrandSession(brand) {
  const user = store.state.user;
  if (!user) return false;

  const sessionBrandKey = user.brand || DEFAULT_BRAND_KEY;
  if (sessionBrandKey !== brand.key) {
    store.dispatch("logout");
    return true;
  }
  return false;
}
